import { useState, useEffect } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

interface AuditEntry {
  id: string;
  ruleId: string | null;
  actionType: string;
  targetType: string | null;
  targetId: string | null;
  details: Record<string, unknown> | null;
  createdAt: Date | string;
}

const ACTION_CONFIG: Record<string, { icon: string; bg: string; text: string; border: string; label: string }> = {
  pause:         { icon: '⏸️', bg: 'bg-rose-500/10',   text: 'text-rose-400',   border: 'border-rose-500/20',   label: 'PAUSED'  },
  enable:        { icon: '▶️', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', label: 'ENABLED' },
  weight_change: { icon: '🔄', bg: 'bg-blue-500/10',   text: 'text-blue-400',   border: 'border-blue-500/20',   label: 'WEIGHT'  },
  alert:         { icon: '⚠️', bg: 'bg-amber-500/10',  text: 'text-amber-400',  border: 'border-amber-500/20',  label: 'ALERT'   },
  default:       { icon: '📋', bg: 'bg-slate-500/10',  text: 'text-slate-400',  border: 'border-slate-500/20',  label: 'EVENT'   },
};

function formatTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    return `${h}h ${m}m ago`;
  }
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatTimestamp(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

function Skeleton() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl flex flex-col animate-pulse">
      <div className="px-5 py-4 border-b border-slate-800 h-12 bg-slate-800/20" />
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="flex items-start gap-4 px-5 py-3.5 border-b border-slate-800/60">
          <div className="w-9 h-9 rounded-lg bg-slate-800" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-slate-800 rounded w-48" />
            <div className="h-3 bg-slate-800 rounded w-64" />
          </div>
          <div className="w-16 h-8 bg-slate-800 rounded" />
        </div>
      ))}
    </div>
  );
}

export function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.guardian.auditLog.query({ tenantId: user.tenantId })
      .then(data => setEntries(data as AuditEntry[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton />;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Audit Log</h3>
        <span className="text-xs text-slate-500">{entries.length} entries · last 24h</span>
      </div>

      {entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <span className="text-3xl">📋</span>
          <p className="text-slate-400 text-sm">No audit events yet</p>
          <p className="text-slate-600 text-xs">Guardian actions will appear here once rules trigger.</p>
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[520px] divide-y divide-slate-800/60">
          {entries.map(entry => {
            const cfg = ACTION_CONFIG[entry.actionType] ?? ACTION_CONFIG.default;
            return (
              <div key={entry.id} className="flex items-start gap-4 px-5 py-3.5 hover:bg-slate-800/40 transition-colors">
                {/* Icon */}
                <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-base ${cfg.bg} border ${cfg.border}`}>
                  {cfg.icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold uppercase tracking-wider ${cfg.text}`}>{cfg.label}</span>
                    {entry.targetId && (
                      <span className="text-xs text-slate-400 truncate">{entry.targetId}</span>
                    )}
                  </div>
                  {entry.details && (
                    <p className="text-sm text-slate-300 mt-0.5 leading-snug font-mono text-xs">
                      {JSON.stringify(entry.details).slice(0, 120)}
                    </p>
                  )}
                </div>

                {/* Time */}
                <div className="flex-shrink-0 text-right">
                  <div className="text-xs font-mono text-slate-400">{formatTimestamp(entry.createdAt)}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{formatTime(entry.createdAt)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
